import React from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { BtLink, BtText } from '@linqpal/components/src/ui'
import NotesComponent from '../../../../Supplier/Invoices/Components/NotesComponent'
import styles from './styles'
import { PROJECT_SECTIONS } from '@linqpal/models/src/dictionaries'
import numbro from 'numbro'
import { composeStyle } from '@linqpal/common-frontend/src/helpers'
import ProjectStore from '../../../../ProjectForm/Store'
import { routes } from '@linqpal/models'
import _ from 'lodash'
import { phoneMaskWithoutCode } from '../../../../../utils/helpers/masking'
import { mapToNewProjectModel } from '../../../mapToNewProjectModel'
import {
  AddressModel,
  FullAddressModel,
} from '@linqpal/models/src/mst/ProjectForm'

const DetailItem = ({ value, label, link = false }) => {
  const downloadFile = () => {
    routes.file.download(value).then(({ url }) => window.open(url, '_blank'))
  }
  return (
    <View style={styles.infoWrapper}>
      {link ? (
        <BtLink
          textStyle={{ color: '#00A0F3', textDecorationLine: 'underline' }}
          onPress={downloadFile}
          title={label}
        />
      ) : (
        <>
          <BtText style={styles.label}>{label}</BtText>
          <BtText
            testID={`projects-detail-view-sidebar-project-name`}
            style={styles.labelInfo}
          >
            {value}
          </BtText>
        </>
      )}
    </View>
  )
}

const getContent = (questionId, items) => {
  return items.find((i) => i.identifier === questionId)?.content
}

const getAddress = (
  addr: typeof AddressModel | typeof FullAddressModel,
): string => {
  let res = ''
  const filteredObj = _.pickBy(addr, _.identity)
  const keys = Object.keys(filteredObj)
  keys.forEach((key, i) => {
    if (addr[key]) {
      const newStr = i === keys.length - 1 ? `${addr[key]}` : `${addr[key]}, `
      res += newStr
    }
  })
  return res
}

const PropertyAddress = ({ items }) => {
  const { t } = useTranslation('global')
  const address = getContent('address', items)
  const jobId = getContent('jobId', items)
  const notice = getContent('noticeOfCommencement', items)
  return (
    <>
      <DetailItem
        label={t(`ProjectSidebar.address`)}
        value={getAddress(address)}
      />
      {jobId && (
        <DetailItem label={t(`ProjectForm.jobid-label`)} value={jobId} />
      )}
      {notice && (
        <DetailItem label={t(`ProjectSidebar.notice`)} value={notice} link />
      )}
    </>
  )
}

const ProjectInformation = ({ items }) => {
  const { t } = useTranslation('global')
  const contractValue = getContent('contractValue', items)
  const timePeriod = getContent('timePeriod', items) ?? {}
  const role = getContent('role', items)
  const contractorDetails = getContent('primeContractorDetails', items)
  const uploadContract = getContent('uploadContract', items)
  return (
    <>
      <DetailItem
        label={t(`ProjectSidebar.value-of-contract`)}
        value={`$${numbro(contractValue).format({
          thousandSeparated: true,
          mantissa: 2,
        })}`}
      />
      <DetailItem
        label={t('ProjectSidebar.first-day')}
        value={timePeriod.firstDayOnJob}
      />
      <DetailItem
        label={t('ProjectSidebar.last-day')}
        value={timePeriod.expectedLastDayOnJob}
      />
      <DetailItem label={t('ProjectSidebar.role')} value={role} />
      {contractorDetails && (
        <>
          <Text style={composeStyle(styles.label, { marginBottom: 20 })}>
            {t('ProjectSidebar.contractorDetails')}
          </Text>
          <DetailItem
            label={t('ProjectSidebar.businessName')}
            value={contractorDetails.businessName}
          />
          <DetailItem
            label={t('ProjectSidebar.firstName')}
            value={contractorDetails.firstName}
          />
          <DetailItem
            label={t('ProjectSidebar.lastName')}
            value={contractorDetails.lastName}
          />
          <DetailItem
            label={t('ProjectSidebar.business-phone')}
            value={phoneMaskWithoutCode(contractorDetails.phone)}
          />
          <DetailItem
            label={t('ProjectSidebar.email')}
            value={contractorDetails.email}
          />
          <DetailItem
            label={t('ProjectSidebar.business-address')}
            value={getAddress(contractorDetails.businessAddress)}
          />
        </>
      )}
      {uploadContract && (
        <DetailItem
          label={t('ProjectSidebar.project-contract')}
          value={uploadContract}
          link
        />
      )}
    </>
  )
}

const ProjectType = ({ items }) => {
  const { t } = useTranslation('global')
  const projectType = getContent('type', items)
  const privateType = getContent('privateType', items)
  const projectBuiltFor = getContent('projectBuiltFor', items)
  const descriptionOfPublicProject = getContent(
    'descriptionOfPublicProject',
    items,
  )
  const bondExists = getContent('bondExists', items)
  const subcontractorShouldCarryBond = getContent(
    'subcontractorShouldCarryBond',
    items,
  )
  const uploadBond = getContent('uploadBond', items)
  return (
    <>
      <DetailItem
        label={t('ProjectSidebar.project-type')}
        value={_.capitalize(projectType)}
      />
      {privateType && (
        <DetailItem
          label={t('ProjectSidebar.private-type')}
          value={_.capitalize(privateType)}
        />
      )}
      {projectBuiltFor && (
        <DetailItem
          label={t('ProjectSidebar.built-for')}
          value={projectBuiltFor}
        />
      )}
      {descriptionOfPublicProject && (
        <DetailItem
          label={t('ProjectSidebar.description-of-public')}
          value={descriptionOfPublicProject}
        />
      )}
      {bondExists && (
        <DetailItem
          label={t('ProjectSidebar.bond-exists')}
          value={_.capitalize(bondExists)}
        />
      )}
      {subcontractorShouldCarryBond && (
        <DetailItem
          label={t('ProjectSidebar.subcontractor-should-carry-bond')}
          value={subcontractorShouldCarryBond}
        />
      )}
      {uploadBond && (
        <DetailItem
          label={t('ProjectSidebar.project-bond')}
          value={uploadBond}
          link
        />
      )}
    </>
  )
}

const PropertyOwner = ({ items }) => {
  const { t } = useTranslation('global')
  const owners = getContent('details', items) ?? []
  const { isBusiness } = ProjectStore
  return (
    <>
      {owners.map((owner, i) => {
        return (
          <React.Fragment key={owner.id}>
            <DetailItem
              label={t(`ProjectSidebar.owner-type`)}
              value={_.capitalize(owner.type)}
            />
            <DetailItem
              label={t(`ProjectSidebar.firstName`)}
              value={owner.firstName}
            />
            <DetailItem
              label={t(`ProjectSidebar.lastName`)}
              value={owner.lastName}
            />
            <DetailItem
              label={
                isBusiness(owner)
                  ? t('ProjectSidebar.business-phone')
                  : t('ProjectSidebar.phone')
              }
              value={phoneMaskWithoutCode(owner.phone)}
            />
            <DetailItem
              label={
                isBusiness(owner)
                  ? t(`ProjectSidebar.business-address`)
                  : t(`ProjectSidebar.home-address`)
              }
              value={getAddress(owner.address)}
            />
            {i !== owners.length - 1 && <View style={styles.underline} />}
          </React.Fragment>
        )
      })}
    </>
  )
}

const Questions = ({ section }) => {
  switch (section.group) {
    case PROJECT_SECTIONS.ADDRESS: {
      return <PropertyAddress items={section.items} />
    }
    case PROJECT_SECTIONS.INFORMATION: {
      return <ProjectInformation items={section.items} />
    }
    case PROJECT_SECTIONS.TYPE: {
      return <ProjectType items={section.items} />
    }
    case PROJECT_SECTIONS.PROPERTY_OWNER: {
      return <PropertyOwner items={section.items} />
    }
    default: {
      return <></>
    }
  }
}

export const ViewForm = ({ chosenProject }) => {
  const project =
    chosenProject.data ??
    mapToNewProjectModel(chosenProject.address, chosenProject.contractValue)
  const sections = Object.values(PROJECT_SECTIONS)

  const getSectionTitle = (section) => {
    const y = section.group.replace(/([a-z])([A-Z])/g, '$1 $2')
    return `${y[0].toUpperCase()}${y.slice(1)}`
  }

  return (
    <View style={{ paddingHorizontal: 20 }}>
      {sections.map((s) => {
        return (
          <View key={s}>
            <BtText style={styles.groupTitle}>
              {getSectionTitle(project[s])}
            </BtText>
            <Questions section={project[s]} />
          </View>
        )
      })}
      <View style={styles.underline} />
      <NotesComponent project={chosenProject} isItInvoiceCreate={false} />
    </View>
  )
}
